/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Slider,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import Cropper from 'react-easy-crop';
import type { Area, Point } from 'react-easy-crop/types';

/**
 * Props for the ImageCropDialog component.
 */
interface ImageCropDialogProps {
  /**
   * Whether the dialog is open.
   */
  open: boolean;
  /**
   * The image URL to crop.
   */
  imageUrl: string;
  /**
   * The aspect ratio for cropping.
   * @default 1 (square)
   */
  aspect?: number;
  /**
   * Whether to show a circular crop area.
   * @default true
   */
  showCircularCrop?: boolean;
  /**
   * The title of the dialog.
   * @default 'Crop Image'
   */
  title?: string;
  /**
   * Callback when the user confirms the crop.
   */
  onConfirm: (croppedAreaPixels: Area) => void;
  /**
   * Callback when the user cancels.
   */
  onCancel: () => void;
  /**
   * Whether the save operation is in progress.
   * @default false
   */
  loading?: boolean;
}

/**
 * ImageCropDialog component that provides image cropping functionality.
 * Supports drag-to-reposition, zoom controls, and both circular and square cropping.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The image crop dialog component
 */
const ImageCropDialog: React.FC<ImageCropDialogProps> = ({
  open,
  imageUrl,
  aspect = 1,
  showCircularCrop = true,
  title = 'Crop Image',
  onConfirm,
  onCancel,
  loading = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Crop state
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  // Refs for keyboard handling
  const cropperRef = useRef<HTMLDivElement>(null);

  /**
   * Handles crop complete callback from react-easy-crop.
   */
  const onCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixelsParam: Area) => {
      setCroppedAreaPixels(croppedAreaPixelsParam);
    },
    []
  );

  /**
   * Handles zoom changes from the slider.
   */
  const handleZoomChange = useCallback(
    (_: Event, newValue: number | number[]) => {
      setZoom(Array.isArray(newValue) ? newValue[0] : newValue);
    },
    []
  );

  /**
   * Handles zoom in button click.
   */
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  }, []);

  /**
   * Handles zoom out button click.
   */
  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 0.1, 1));
  }, []);

  /**
   * Resets crop position and zoom.
   */
  const handleReset = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  /**
   * Handles confirm button click.
   */
  const handleConfirm = useCallback(() => {
    if (croppedAreaPixels) {
      onConfirm(croppedAreaPixels);
    }
  }, [croppedAreaPixels, onConfirm]);

  /**
   * Handles keyboard shortcuts.
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && croppedAreaPixels) {
        event.preventDefault();
        handleConfirm();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
      }
    },
    [croppedAreaPixels, handleConfirm, onCancel]
  );

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100vh' : '80vh',
          maxHeight: isMobile ? '100vh' : '80vh',
        },
      }}
      onKeyDown={handleKeyDown}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        {title}
        <IconButton
          onClick={onCancel}
          size="small"
          disabled={loading}
          aria-label="Close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden' }}>
        {/* Cropper Container */}
        <Box
          ref={cropperRef}
          sx={{
            position: 'relative',
            width: '100%',
            height: isMobile ? 'calc(100vh - 200px)' : '400px',
            backgroundColor: 'black',
          }}
        >
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onCropComplete={onCropComplete}
            onZoomChange={setZoom}
            showGrid={!showCircularCrop}
            cropShape={showCircularCrop ? 'round' : 'rect'}
            style={{
              containerStyle: {
                width: '100%',
                height: '100%',
              },
            }}
          />
        </Box>

        {/* Controls */}
        <Box
          sx={{
            p: 2,
            backgroundColor: 'background.paper',
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Zoom Controls */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              Zoom
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                size="small"
                onClick={handleZoomOut}
                disabled={zoom <= 1 || loading}
                aria-label="Zoom out"
              >
                <ZoomOutIcon />
              </IconButton>
              <Slider
                value={zoom}
                min={1}
                max={3}
                step={0.1}
                onChange={handleZoomChange}
                disabled={loading}
                sx={{ flex: 1 }}
                aria-label="Zoom level"
              />
              <IconButton
                size="small"
                onClick={handleZoomIn}
                disabled={zoom >= 3 || loading}
                aria-label="Zoom in"
              >
                <ZoomInIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Additional Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              startIcon={<ResetIcon />}
              onClick={handleReset}
              disabled={loading}
              size="small"
            >
              Reset Position
            </Button>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!croppedAreaPixels || loading}
          autoFocus
        >
          {loading ? 'Processing...' : 'Save'}
        </Button>
      </DialogActions>

      {/* Help Text */}
      {!isMobile && (
        <Box sx={{ px: 3, pb: 2, pt: 0 }}>
          <Typography variant="caption" color="text.secondary">
            Drag to reposition • Scroll or use slider to zoom • Enter to save •
            Escape to cancel
          </Typography>
        </Box>
      )}
    </Dialog>
  );
};

export default ImageCropDialog;
